import { collisionOutcome } from '@physlab/core';
import { createQuestion, fmt, selectTemplates } from '../questionUtils.js';

export function buildCollisionQuestions({ params, rng, difficultyLevel, exerciseCount, thinkingCount }) {
  const m1 = params.mass1;
  const m2 = params.mass2;
  const v1 = params.velocity1;
  const v2 = Math.min(params.velocity2, v1 - 0.5);
  const e = params.restitution;
  const totalMass = m1 + m2;
  const momentum = m1 * v1 + m2 * v2;
  const energyBefore = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;
  const com = momentum / totalMass;
  const elastic = collisionOutcome(m1, m2, v1, v2, 1);
  const eQuestion = e < 0.999 ? e : 0.6;
  const inelastic = collisionOutcome(m1, m2, v1, v2, eQuestion);
  const afterEnergy = 0.5 * m1 * inelastic.v1 ** 2 + 0.5 * m2 * inelastic.v2 ** 2;
  const energyLoss = energyBefore - afterEnergy;
  const combined = momentum / totalMass;
  const eData = 0.6;
  const measured = collisionOutcome(m1, m2, v1, v2, eData);

  const exercises = [
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `collision-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '完全弹性碰撞的速度',
          prompt: `光滑水平面上，滑块 A、B 质量分别为 $m_1=${fmt(m1, 1)}\\ \\mathrm{kg}$、$m_2=${fmt(m2, 1)}\\ \\mathrm{kg}$，碰前速度 $v_1=${fmt(v1, 1)}\\ \\mathrm{m/s}$、$v_2=${fmt(v2, 1)}\\ \\mathrm{m/s}$（向右为正）。发生一维完全弹性碰撞，求碰后速度 $v_1\'$、$v_2\'$。`,
          formula: 'v_1\'=\\dfrac{(m_1-m_2)v_1+2m_2v_2}{m_1+m_2},\\quad v_2\'=\\dfrac{(m_2-m_1)v_2+2m_1v_1}{m_1+m_2}',
          answer: `$v_1\'\\approx ${fmt(elastic.v1, 2)}\\ \\mathrm{m/s}$，$v_2\'\\approx ${fmt(elastic.v2, 2)}\\ \\mathrm{m/s}$`,
          solution: [
            `完全弹性碰撞 $e=1$，代入一维碰撞公式：$v_1\'=\\dfrac{(${fmt(m1, 1)}-${fmt(m2, 1)})\\times${fmt(v1, 1)}+2\\times${fmt(m2, 1)}\\times(${fmt(v2, 1)})}{${fmt(totalMass, 1)}}$。`,
            `计算得 $v_1\'\\approx ${fmt(elastic.v1, 3)}\\ \\mathrm{m/s}$。同理 $v_2\'\\approx ${fmt(elastic.v2, 3)}\\ \\mathrm{m/s}$。`,
            `可用动量守恒校验：碰前 $p=${fmt(momentum, 2)}\\ \\mathrm{kg\\cdot m/s}$，碰后 $p\'=${fmt(elastic.momentum, 2)}\\ \\mathrm{kg\\cdot m/s}$，二者相等。`,
          ],
          knowledge: ['完全弹性碰撞', '动量守恒', '一维碰撞公式'],
        }),
    },
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `collision-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '系统总动量',
          prompt: `质量 $m_1=${fmt(m1, 1)}\\ \\mathrm{kg}$ 的滑块以 $v_1=${fmt(v1, 1)}\\ \\mathrm{m/s}$ 向右运动，质量 $m_2=${fmt(m2, 1)}\\ \\mathrm{kg}$ 的滑块以 $v_2=${fmt(v2, 1)}\\ \\mathrm{m/s}$ 运动。求碰撞前系统的总动量 $p$（向右为正）。`,
          formula: 'p=m_1v_1+m_2v_2',
          answer: `$p=${fmt(momentum, 2)}\\ \\mathrm{kg\\cdot m/s}$`,
          answerValue: momentum,
          unit: 'kg·m/s',
          solution: [
            `规定向右为正方向，注意 $v_2$ 的符号：$p=m_1v_1+m_2v_2$。`,
            `代入数据：$p=${fmt(m1, 1)}\\times${fmt(v1, 1)}+${fmt(m2, 1)}\\times(${fmt(v2, 1)})=${fmt(momentum, 2)}\\ \\mathrm{kg\\cdot m/s}$。`,
            `结果为正，表示总动量方向向右。`,
          ],
          knowledge: ['动量', '正方向的规定'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `collision-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '碰撞中的动能损失',
          prompt: `两滑块质量 $m_1=${fmt(m1, 1)}\\ \\mathrm{kg}$、$m_2=${fmt(m2, 1)}\\ \\mathrm{kg}$，碰前速度 $v_1=${fmt(v1, 1)}\\ \\mathrm{m/s}$、$v_2=${fmt(v2, 1)}\\ \\mathrm{m/s}$。若恢复系数 $e=${fmt(eQuestion, 2)}$，求碰后总动能 $E_k\'$ 与损失的动能 $\\Delta E_k$。`,
          formula: '\\Delta E_k=\\dfrac{m_1m_2}{2(m_1+m_2)}(1-e^2)(v_1-v_2)^2',
          answer: `$E_k\'\\approx ${fmt(afterEnergy, 2)}\\ \\mathrm{J}$，$\\Delta E_k\\approx ${fmt(energyLoss, 2)}\\ \\mathrm{J}$`,
          solution: [
            `先求碰前总动能：$E_k=\\tfrac12m_1v_1^2+\\tfrac12m_2v_2^2\\approx ${fmt(energyBefore, 2)}\\ \\mathrm{J}$。`,
            `由恢复系数公式求碰后速度：$v_1\'\\approx ${fmt(inelastic.v1, 3)}\\ \\mathrm{m/s}$，$v_2\'\\approx ${fmt(inelastic.v2, 3)}\\ \\mathrm{m/s}$。`,
            `碰后总动能 $E_k\'\\approx ${fmt(afterEnergy, 3)}\\ \\mathrm{J}$，损失 $\\Delta E_k=E_k-E_k\'\\approx ${fmt(energyLoss, 3)}\\ \\mathrm{J}$。`,
            `也可直接用公式 $\\Delta E_k=\\dfrac{m_1m_2}{2(m_1+m_2)}(1-e^2)(v_1-v_2)^2$ 校验。`,
          ],
          knowledge: ['恢复系数', '动能损失', '能量转化'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `collision-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '由实验数据求恢复系数',
          prompt: `两滑块质量 $m_1=${fmt(m1, 1)}\\ \\mathrm{kg}$、$m_2=${fmt(m2, 1)}\\ \\mathrm{kg}$，碰前 $v_1=${fmt(v1, 1)}\\ \\mathrm{m/s}$、$v_2=${fmt(v2, 1)}\\ \\mathrm{m/s}$。实验测得碰后 $v_1\'=${fmt(measured.v1, 2)}\\ \\mathrm{m/s}$、$v_2\'=${fmt(measured.v2, 2)}\\ \\mathrm{m/s}$。求恢复系数 $e$，并判断碰撞类型。`,
          formula: 'e=\\dfrac{v_2\'-v_1\'}{v_1-v_2}',
          answer: `$e\\approx ${fmt((measured.v2 - measured.v1) / (v1 - v2), 2)}$，属于非弹性碰撞`,
          solution: [
            `恢复系数定义为分离速度与接近速度之比：$e=\\dfrac{v_2\'-v_1\'}{v_1-v_2}$。`,
            `代入：$e=\\dfrac{${fmt(measured.v2, 2)}-(${fmt(measured.v1, 2)})}{${fmt(v1, 1)}-(${fmt(v2, 1)})}\\approx ${fmt((measured.v2 - measured.v1) / (v1 - v2), 3)}$。`,
            `因为 $0<e<1$，所以是非弹性碰撞，动量守恒但动能有损失。`,
          ],
          knowledge: ['恢复系数', '碰撞分类', '实验数据处理'],
        }),
    },
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `collision-ex-${index}`,
          type: 'exercise',
          level: 1,
          topic: '质心速度',
          prompt: `系统由质量 $m_1=${fmt(m1, 1)}\\ \\mathrm{kg}$、$m_2=${fmt(m2, 1)}\\ \\mathrm{kg}$ 的两滑块组成，速度分别为 $v_1=${fmt(v1, 1)}\\ \\mathrm{m/s}$、$v_2=${fmt(v2, 1)}\\ \\mathrm{m/s}$。求系统质心速度 $v_{\\mathrm{cm}}$。`,
          formula: 'v_{\\mathrm{cm}}=\\dfrac{m_1v_1+m_2v_2}{m_1+m_2}',
          answer: `$v_{\\mathrm{cm}}\\approx ${fmt(com, 2)}\\ \\mathrm{m/s}$`,
          answerValue: com,
          unit: 'm/s',
          solution: [
            `总动量 $p=${fmt(momentum, 2)}\\ \\mathrm{kg\\cdot m/s}$，总质量 $M=${fmt(totalMass, 1)}\\ \\mathrm{kg}$。`,
            `$v_{\\mathrm{cm}}=\\dfrac{p}{M}=\\dfrac{${fmt(momentum, 2)}}{${fmt(totalMass, 1)}}\\approx ${fmt(com, 3)}\\ \\mathrm{m/s}$。`,
            `碰撞过程中系统不受水平外力，质心速度保持不变。`,
          ],
          knowledge: ['质心速度', '动量守恒'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `collision-ex-${index}`,
          type: 'exercise',
          level: 2,
          topic: '完全非弹性碰撞',
          prompt: `质量 $m_1=${fmt(m1, 1)}\\ \\mathrm{kg}$、$m_2=${fmt(m2, 1)}\\ \\mathrm{kg}$ 的两滑块碰前速度分别为 $v_1=${fmt(v1, 1)}\\ \\mathrm{m/s}$、$v_2=${fmt(v2, 1)}\\ \\mathrm{m/s}$。若碰撞后粘在一起，求共同速度 $v$ 和损失的动能。`,
          formula: 'v=\\dfrac{m_1v_1+m_2v_2}{m_1+m_2}',
          answer: `$v\\approx ${fmt(combined, 2)}\\ \\mathrm{m/s}$，损失约 $${fmt(energyBefore - 0.5 * totalMass * combined * combined, 2)}\\ \\mathrm{J}$`,
          solution: [
            `完全非弹性碰撞中两物体碰后同速，由动量守恒：$m_1v_1+m_2v_2=(m_1+m_2)v$。`,
            `$v=\\dfrac{${fmt(momentum, 2)}}{${fmt(totalMass, 1)}}\\approx ${fmt(combined, 3)}\\ \\mathrm{m/s}$。`,
            `碰后动能 $E_k\'=\\tfrac12(m_1+m_2)v^2\\approx ${fmt(0.5 * totalMass * combined * combined, 2)}\\ \\mathrm{J}$，损失 $\\Delta E_k=${fmt(energyBefore, 2)}-${fmt(0.5 * totalMass * combined * combined, 2)}\\approx ${fmt(energyBefore - 0.5 * totalMass * combined * combined, 2)}\\ \\mathrm{J}$。`,
          ],
          knowledge: ['完全非弹性碰撞', '动量守恒', '动能损失'],
        }),
    },
  ];

  const thinking = [
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `collision-th-${index}`,
          type: 'thinking',
          level: 1,
          topic: '动量守恒的条件',
          prompt: '碰撞过程中两滑块之间的作用力很大，为什么系统总动量仍可近似守恒？请说明适用条件。',
          formula: '\\sum F_{\\text{外}}\\Delta t\\approx0',
          answer:
            '碰撞时间极短，内力远大于外力，外力的冲量可以忽略，因此系统总动量近似守恒。严格守恒要求水平方向合外力为零。',
          solution: [
            '动量定理：$\\Delta p=F_{\\text{合}}\\Delta t$。',
            '碰撞时间 $\\Delta t$ 极短，而内力很大；若外力（如摩擦、空气阻力）远小于内力，其冲量可忽略。',
            '本实验中气垫导轨调平后水平方向无外力，故总动量守恒。若有明显外力冲量，则不能直接用动量守恒。',
          ],
          knowledge: ['动量守恒条件', '冲量', '内力与外力'],
        }),
    },
    {
      level: 1,
      build: (index) =>
        createQuestion({
          id: `collision-th-${index}`,
          type: 'thinking',
          level: 1,
          topic: '等质量弹性碰撞',
          prompt: '两个质量相等的滑块发生一维完全弹性碰撞（碰前 B 静止）。请证明碰后两者交换速度，并说明该结论的实际用途。',
          formula: 'm_1=m_2=m',
          answer:
            '由动量守恒和动能守恒联立，可得 v₁′=0、v₂′=v₁，即速度交换。核反应堆中的中子慢化、台球中的正碰都利用了这一规律。',
          solution: [
            '动量守恒：$mv_1=mv_1\'+mv_2\'$，即 $v_1=v_1\'+v_2\'$。',
            '弹性碰撞动能守恒：$v_1^2=v_1\'^2+v_2\'^2$。',
            '联立两式可得 $v_1\'=0$、$v_2\'=v_1$（舍去未碰撞的解）。',
            '用途：用轻水中的氢核使中子迅速减速；台球正碰时母球把速度传给目标球。',
          ],
          knowledge: ['等质量弹性碰撞', '速度交换', '动量与能量守恒'],
        }),
    },
    {
      level: 2,
      build: (index) =>
        createQuestion({
          id: `collision-th-${index}`,
          type: 'thinking',
          level: 2,
          topic: '动量守恒但动能不守恒',
          prompt: '碰撞过程中总动量可以严格守恒，但总动能不一定守恒。两者是否矛盾？请结合能量转化说明。',
          formula: 'p_{\\text{前}}=p_{\\text{后}},\\quad E_{k,\\text{后}}\\le E_{k,\\text{前}}',
          answer:
            '不矛盾。动量是矢量，其守恒由外力冲量决定；动能是标量，非弹性碰撞中部分动能转化为内能、声能等，因此可以减少，但不能凭空增加。',
          solution: [
            '动量守恒来自系统所受合外力冲量为零，是一个矢量守恒定律。',
            '动能可以转化为其他形式的能量（内能、形变能、声能），所以非弹性碰撞后总动能减少。',
            '若无外力做正功，碰后总动能不会大于碰前；完全弹性碰撞是动能不损失的理想极限。',
          ],
          knowledge: ['动量与动能的区别', '非弹性碰撞', '能量守恒'],
        }),
    },
    {
      level: 3,
      build: (index) =>
        createQuestion({
          id: `collision-th-${index}`,
          type: 'thinking',
          level: 3,
          topic: '实验方案与误差',
          prompt: '用气垫导轨验证动量守恒时，为什么必须调平导轨？如果导轨右端略高，会对碰撞前后的动量测量产生什么影响？',
          formula: '\\Delta p=F_{\\text{外}}\\Delta t',
          answer:
            '导轨不水平时，重力沿导轨的分量成为外力，会产生额外冲量，使系统动量不守恒。右端偏高时滑块向右加速，碰后总动量会偏大。',
          solution: [
            '调平后，滑块在水平方向所受合外力近似为零，满足动量守恒条件。',
            '若右端偏高，重力沿导轨的分量向右，滑块向右运动时加速，外力冲量使总动量增加。',
            '可通过观察滑块在导轨上是否匀速运动来检查调平；也可用光电门测多段速度，若速度系统增大则说明未调平。',
          ],
          knowledge: ['实验误差分析', '动量守恒条件', '气垫导轨'],
        }),
    },
  ];

  const selectedExercises = selectTemplates(rng, exercises, exerciseCount, difficultyLevel);
  const selectedThinking = selectTemplates(rng, thinking, thinkingCount, difficultyLevel);
  return {
    exercises: selectedExercises.map((template, index) => template.build(index + 1)),
    thinking: selectedThinking.map((template, index) => template.build(index + 1)),
  };
}
